import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Library, Plus, Star, Trash2, ExternalLink, Loader2, Upload, FileText } from 'lucide-react';

const API_URL = window.location.origin;
const mediaUrl = (url) => (url?.startsWith('http') ? url : `${API_URL}${url}`);

export const LegacyResources = ({ user }) => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', url: '', description: '', category: 'Other' });
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoInputRef = useRef(null);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error('File must be under 50MB'); return; }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await axios.post(`${API_URL}/api/legacy/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, url: res.data.url }));
      toast.success('File uploaded — add a title and share it 📎');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchResources = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/resources`);
      setResources(res.data.resources);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to load resources:', error);
    }
  };

  useEffect(() => { fetchResources(); }, []);

  const handleAdd = async () => {
    if (!form.title.trim()) { toast.error('Give the resource a title'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/legacy/resources`, form);
      toast.success('Resource shared with the circle 📚');
      setForm({ title: '', url: '', description: '', category: 'Other' });
      setShowForm(false);
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this resource?')) return;
    try {
      await axios.delete(`${API_URL}/api/legacy/resources/${id}`);
      toast.success('Resource removed');
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleFeature = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/legacy/resources/${id}/feature`);
      toast.success(res.data.featured ? 'Featured ⭐' : 'Unfeatured');
      fetchResources();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const visible = filter === 'all' ? resources : resources.filter(r => r.category === filter);

  return (
    <div className="space-y-6" data-testid="legacy-resources">
      <Card className="card-base border-terracotta/20 bg-gradient-to-br from-terracotta/5 to-transparent">
        <CardContent className="p-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Library className="h-5 w-5 text-terracotta" />
              <p className="text-xs uppercase tracking-widest text-terracotta">Resource Library</p>
            </div>
            <p className="font-heading text-xl text-forest">Books, tools, and wisdom the circle is building with.</p>
          </div>
          <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-forest hover:bg-forest/90 text-white" data-testid="resource-add-toggle">
            <Plus className="h-4 w-4 mr-2" /> Share a Resource
          </Button>
        </CardContent>
      </Card>

      {showForm && (
        <Card className="card-base">
          <CardContent className="p-4 space-y-3">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title *" data-testid="resource-title-input" />
            <div className="flex gap-2">
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Link — YouTube, Vimeo, article, or any URL (optional)" data-testid="resource-url-input" className="flex-1" />
              <Button type="button" variant="outline" onClick={() => videoInputRef.current?.click()} disabled={uploadingVideo} className="flex-shrink-0" data-testid="resource-video-upload-btn">
                {uploadingVideo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Upload File
              </Button>
              <input ref={videoInputRef} type="file"
                accept="video/mp4,video/quicktime,image/*,.mp4,.mov,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv"
                className="hidden" onChange={handleVideoUpload} data-testid="resource-video-input" />
            </div>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Why it matters (optional)" rows={2} data-testid="resource-description-input" />
            <div className="flex items-center justify-between gap-3">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="max-w-xs" data-testid="resource-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={handleAdd} disabled={saving} className="bg-forest hover:bg-forest/90 text-white" data-testid="resource-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Add
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter('all')} className={`text-xs px-3 py-1.5 rounded-full border ${filter === 'all' ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`} data-testid="resource-filter-all">
          All
        </button>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === c ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`}>
            {c}
          </button>
        ))}
      </div>

      {/* List */}
      {visible.length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {visible.map(r => (
            <Card key={r.id} className={`card-base ${r.featured ? 'border-champagne bg-champagne/10' : ''}`} data-testid={`resource-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.featured && <Star className="h-4 w-4 text-terracotta fill-terracotta flex-shrink-0" />}
                      <p className="font-medium text-forest">{r.title}</p>
                    </div>
                    <Badge variant="outline" className="text-xs mt-1 text-gray-500">{r.category}</Badge>
                    {r.description && <p className="text-sm text-gray-600 mt-2">{r.description}</p>}
                    {r.media_type === 'youtube' || r.media_type === 'vimeo' ? (
                      <div className="mt-3 rounded-xl overflow-hidden aspect-video">
                        <iframe src={r.embed_url} title={r.title} className="w-full h-full" frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid={`resource-embed-${r.id}`} />
                      </div>
                    ) : r.media_type === 'video' ? (
                      <video controls src={mediaUrl(r.embed_url)} className="mt-3 rounded-xl w-full max-h-72" data-testid={`resource-video-${r.id}`} />
                    ) : r.media_type === 'image' ? (
                      <img src={mediaUrl(r.embed_url)} alt={r.title} className="mt-3 rounded-xl w-full max-h-72 object-cover" data-testid={`resource-image-${r.id}`} />
                    ) : r.media_type === 'document' ? (
                      <Button size="sm" variant="outline" className="mt-3" onClick={() => window.open(mediaUrl(r.embed_url), '_blank')} data-testid={`resource-document-${r.id}`}>
                        <FileText className="h-4 w-4 mr-2 text-terracotta" /> View Document
                      </Button>
                    ) : null}
                    <p className="text-xs text-gray-400 mt-2">Shared by {r.added_by_name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {r.url && (
                      <Button size="sm" variant="ghost" onClick={() => window.open(r.url, '_blank')} data-testid={`resource-open-${r.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => handleFeature(r.id)} data-testid={`resource-feature-${r.id}`}>
                        <Star className={`h-4 w-4 ${r.featured ? 'text-terracotta fill-terracotta' : 'text-gray-400'}`} />
                      </Button>
                    )}
                    {(isAdmin || r.added_by === user?.id) && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} data-testid={`resource-delete-${r.id}`}>
                        <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-6">No resources in this category yet. Share the first one.</p>
      )}
    </div>
  );
};

export default LegacyResources;
